import toMarkdown from "to-markdown";
import Showdown from "showdown";
import { urlUtils } from "@ardoq/common-helpers";
import { escapeHTML, sanitize } from "@ardoq/html";
import { jsx } from "react/jsx-runtime";
import { forwardRef } from "react";
import { colors, spacing } from "@ardoq/design-tokens";
import { typographyMixins, normal16 } from "@ardoq/typography";
import styled from "styled-components";
function escapeMarkdownCharactersIfNeeded(html) {
  const htmlTagRegex = /(<.+?>)/gm;
  let nestedCodeLevel = 0;
  let nestedPreLevel = 0;
  return html.split(htmlTagRegex).map((part) => {
    if (part.match(htmlTagRegex)) {
      if (part.match(/<code/)) nestedCodeLevel++;
      if (part.match(/<\/code/)) nestedCodeLevel--;
      if (part.match(/<pre/)) nestedPreLevel++;
      if (part.match(/<\/pre/)) nestedPreLevel--;
      return part;
    } else if (nestedCodeLevel > 0 || nestedPreLevel > 0) {
      return part;
    }
    return escapeMDText(part);
  }).join("");
}
function fixNestedListsAndWhitespaceInEmphasisNodes(html) {
  const nbsp = " ";
  const nodeContainer = document.createElement("wrapper");
  nodeContainer.innerHTML = html.replace(/&nbsp;/gm, nbsp);
  const emphasisNodes = Array.from(
    nodeContainer.querySelectorAll("b, em, i, strike, strong")
  );
  const whitespaceNodes = emphasisNodes.filter(hasLeadingOrTrailingWhitespace);
  const newLineNodes = emphasisNodes.filter(hasOnlyNewLines);
  whitespaceNodes.forEach(moveWhitespaceOutsideOfNode);
  newLineNodes.forEach(replaceWithBr);
  const topSubLists = getTopSubLists(nodeContainer.querySelectorAll("ol, ul"));
  topSubLists.forEach(fixNestedLists);
  return nodeContainer.innerHTML.replace(/\u00A0/gm, "&nbsp;");
}
function escapeMDText(mdText) {
  const filters = [
    {
      label: "escape backslash",
      matcher: /\\/gm,
      replacer: "\\\\"
    },
    {
      label: "escape markdown characters, minus dot, pound and backslash, + pipe: tables",
      matcher: /(?:\\)?(`|\*|_|{|}|\[|]|\(|\)|\+|!|~|\|)/gm,
      replacer: "\\$1"
    },
    {
      label: "escape pound - take care not to escape #tags",
      matcher: /#(?!\w+)|^#(?=\w+)/gm,
      replacer: "\\#"
    },
    {
      label: "orderedList",
      matcher: /^((?:&nbsp;|\s)*\d+)\.(&nbsp;|\s)+/gm,
      replacer: "$1\\.$2",
      match: 0,
      prefixWhitespace: 1,
      suffixWhitespace: 2
    }
  ];
  return filters.reduce(
    (text, filter2) => text.replace(filter2.matcher, filter2.replacer),
    mdText
  );
}
function hasLeadingOrTrailingWhitespace(node) {
  return node.textContent && node.textContent.trim().length !== node.textContent.length;
}
function hasOnlyNewLines(node) {
  const matcher = /<(b|em|i|strike|strong)><br><\/\1>/gm;
  return matcher.test(node.outerHTML);
}
const moveWhitespaceOutsideOfNode = (node) => {
  const matcher = /^([\s\u00A0]*)(.+?)([\s\u00A0]*)$/gm;
  const result = matcher.exec(node.textContent || "");
  if (result) {
    const [, leadingWhitespace, trimmedContent, trailingWhitespace] = result;
    if (leadingWhitespace) {
      node.insertAdjacentText("beforebegin", leadingWhitespace);
    }
    node.innerHTML = trimmedContent;
    if (trailingWhitespace) {
      node.insertAdjacentText("afterend", trailingWhitespace);
    }
  }
};
function replaceWithBr(node) {
  node.outerHTML = "<br>";
}
function getTopSubLists(lists) {
  const topLists = Array.from(lists);
  lists.forEach((outerElement) => {
    lists.forEach((innerElement) => {
      const listIndex = topLists.indexOf(innerElement);
      if (listIndex > -1 && outerElement !== innerElement && outerElement.contains(innerElement)) {
        topLists.splice(listIndex, 1);
      }
    });
  });
  return topLists;
}
function fixNestedLists(element) {
  const listNodes = Array.from(element.querySelectorAll("ol, ul"));
  listNodes.forEach((listNode) => {
    const parent = listNode.parentNode;
    if (parent && parent.tagName === "LI") {
      return;
    }
    const prev = listNode.previousElementSibling;
    if (prev && prev.matches("li")) {
      prev.appendChild(listNode);
    } else {
      const li = document.createElement("li");
      parent.insertBefore(li, listNode);
      li.appendChild(listNode);
    }
  });
}
function getAlignment(alignment) {
  switch (alignment) {
    case "center":
      return ":---:";
    case "right":
      return "---:";
    case "left":
      return ":---";
    default:
      return "---";
  }
}
function fixBreaksBeforeLists(html) {
  var _a;
  const nodeContainer = document.createElement("wrapper");
  nodeContainer.innerHTML = html;
  const listsAfterBreaks = nodeContainer.querySelectorAll("br + ol, br + ul");
  for (const list of listsAfterBreaks) {
    (_a = list.previousElementSibling) == null ? void 0 : _a.remove();
  }
  return nodeContainer.innerHTML;
}
const getContentWithoutBackticks = (content) => {
  return content.replace(/^([\s]*?)`([\s\S]*?)`([\s]*?)$/g, "$1$2$3");
};
const htmlToMarkdown = (html) => {
  const nodeFixed = fixNestedListsAndWhitespaceInEmphasisNodes(html);
  const listsFixed = fixBreaksBeforeLists(nodeFixed);
  const escapedText = escapeMarkdownCharactersIfNeeded(listsFixed);
  const brReplacement = new RegExp("§br§", "gm");
  const converters = [
    {
      filter: "code",
      replacement: (_content, node) => {
        return Array.from(node.childNodes).reduce((acc, child) => {
          const trimedAcc = acc.substring(0, acc.length - 1);
          if (child.nodeType === Node.TEXT_NODE) {
            return `${trimedAcc}${child.nodeValue}\``;
          }
          if (child.nodeType === Node.ELEMENT_NODE) {
            return `${trimedAcc}\`${htmlToMarkdown(
              child.outerHTML
            )}\`\``;
          }
          return acc;
        }, "``");
      }
    },
    {
      filter: "br",
      replacement: () => brReplacement.source
    },
    {
      filter: "a",
      replacement: (_content, node) => {
        const text = node.textContent, title = node.title, titleAttr = title ? ` "${title}"` : "", href = node.getAttribute("href");
        if (node.classList.contains("inlineComp")) {
          const idRegex = {
            matcher: /.*\/workspace\/(.*)\/component\/(.*)/gm,
            id: 2,
            wsid: 1
          };
          const result = idRegex.matcher.exec(href || "");
          const id = result && result[idRegex.id];
          const wsid = result && result[idRegex.wsid];
          if (wsid) {
            return `[${text}](comp://${wsid}/${id}${titleAttr})`;
          }
        }
        return `[${text}](${href}${titleAttr})`;
      }
    },
    {
      filter: ["font", "span"],
      replacement: (innerHtml, node) => {
        const foregroundColor = node.style.color || node.color;
        const backgroundColor = node.style["background-color"];
        if (!foregroundColor && !backgroundColor) {
          return innerHtml;
        }
        const attributes = [foregroundColor || "", backgroundColor || ""].join(
          "/"
        );
        return `{color:${attributes}}${innerHtml}{color}`;
      }
    },
    {
      filter: "iframe",
      replacement: (_content, node) => {
        const src = node.getAttribute("src"), iframeRegex = /.+(vimeo|youtube).*\/(.*)/gm, iframeParams = iframeRegex.exec(src || "");
        return `[${iframeParams[1]}:${iframeParams[2]}]`;
      }
    },
    {
      filter: "th",
      replacement: (content) => {
        return `| ${content.trim()} `;
      }
    },
    {
      filter: "td",
      replacement: (content) => {
        return `| ${content.trim()} `;
      }
    },
    {
      filter: "tr",
      replacement: (content, node) => {
        var _a;
        const cells = content.split("|").slice(1);
        const formattedCells = cells.map((cell) => cell.trim()).join(" | ");
        const row = `| ${formattedCells} |`;
        if (((_a = node == null ? void 0 : node.parentNode) == null ? void 0 : _a.nodeName) === "THEAD") {
          const alignments = Array.from(node.children).map(
            (child) => getAlignment(child.getAttribute("align"))
          );
          const alignmentRow = `| ${alignments.join(" | ")} |`;
          return `
${row}
${alignmentRow}`;
        }
        return `
${row}`;
      }
    },
    {
      filter: "table",
      replacement: (content) => {
        return `
${content}
`;
      }
    },
    {
      filter: "img",
      replacement: (_content, node) => {
        const alt = node.alt || "";
        const src = node.getAttribute("src") || "";
        const title = node.title || "";
        const titlePart = title ? ` "${title}"` : "";
        const width = node.getAttribute("width");
        const height = node.getAttribute("height");
        const dimPart = width && height ? ` =${width}x${height}` : "";
        return src ? `![${alt}](${src}${dimPart}${titlePart})` : "";
      }
    },
    {
      filter: (node) => {
        var _a;
        return node.nodeName === "PRE" && ((_a = node.firstChild) == null ? void 0 : _a.nodeName) === "CODE";
      },
      replacement: function(_content, node) {
        const classNames = node.firstChild.getAttribute("class"), langMatch = classNames && classNames.match(/lang-(\S+)/), lang = langMatch ? langMatch[1] : "";
        return `

\`\`\`${lang}
${getContentWithoutBackticks(
          node.firstChild.textContent
        )}
\`\`\`

`;
      }
    },
    {
      filter: (node) => {
        var _a;
        return node.nodeName === "PRE" && !(((_a = node.firstChild) == null ? void 0 : _a.nodeName) === "CODE");
      },
      replacement: (_content, node) => {
        const container = document.createElement("div");
        container.innerHTML = node.innerHTML.replace(
          /<br\s*\/?>/gim,
          brReplacement.source
        );
        return `

\`\`\`
${container.textContent.replace(
          brReplacement,
          "\n"
        )}
\`\`\`

`;
      }
    }
  ];
  const markdown = toMarkdown(escapedText, { gfm: true, converters });
  return markdown.replace(brReplacement, "  \n").replace(/\u00A0/gm, " ");
};
const simpleXssValidator = {
  validateColor: (color) => {
    let validate = false;
    if (color) {
      const matchers = [
        {
          label: "rgb",
          matcher: /rgb\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)/gim
        },
        {
          label: "rgba",
          matcher: /rgba\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3}), ?([01]?.?\d{1,3})\)/gim
        },
        {
          label: "hex",
          matcher: /#[0-9A-F]{3,6}/gim
        },
        {
          label: "hsl",
          matcher: /hsl\((\d{1,3}), ?(\d{1,3}%), ?(\d{1,3}%)\)/gim
        },
        {
          label: "hsla",
          matcher: /hsla\((\d{1,3}), ?(\d{1,3}%), ?(\d{1,3}%), ?([01]?.?\d{1,3})\)/gim
        },
        {
          label: "plaintext color",
          matcher: /^[a-zA-Z]+$/g
        }
      ];
      matchers.forEach((item) => {
        if (!validate && item.matcher.test(color)) {
          validate = true;
        }
      });
    }
    return validate;
  }
};
const htmlStringToDomNode = (htmlString) => {
  const container = document.createElement("div");
  container.innerHTML = htmlString.trim();
  return container.firstChild;
};
const showdownMatchers = {
  codeHTMLSanitizer: {
    matcher: /([`]{3}[\S]*?[\s]+)([\s\S]+?)([\s]+[`]{3})/gm,
    replacer: function(match, openingBackticks, code, closingBackticks) {
      return `${openingBackticks}${sanitize(
        code,
        "permissive"
      )}${closingBackticks}`;
    }
  },
  codeTags: {
    matcher: /<code class="(.*?)">([\s\S]+?)<\/code>/gm,
    replacer: function(match, language, code) {
      if (language.split(/\s/).length > 1 && /language-/.test(language)) {
        const extractedLanguage = language.split("language-").pop();
        return `<code data-language="${extractedLanguage}" class="prettyprint lang-${extractedLanguage}">${code}</code>`;
      }
      return `<code data-language="${language}" class="prettyprint lang-${language}">${code}</code>`;
    }
  },
  escapedStrikethrough: {
    matcher: /\\~\\~(.*?)\\~\\~/gm,
    replacer: "~~$1~~"
  },
  escapedPipe: {
    matcher: /\\\|/gm,
    replacer: "|"
  }
};
const ardoqExtension = [
  {
    type: "lang",
    regex: /&/g,
    replace: () => "&amp;"
  },
  {
    type: "lang",
    filter: function(source) {
      return source.replace(
        showdownMatchers.codeHTMLSanitizer.matcher,
        showdownMatchers.codeHTMLSanitizer.replacer
      );
    }
  },
  {
    type: "lang",
    regex: /</g,
    replace: () => "&lt;"
  },
  {
    type: "lang",
    regex: /([^>\n]+)>/g,
    replace: "$1&gt;"
  },
  {
    type: "output",
    regex: /\[(youtube|vimeo):(.+?)]/gm,
    replace: function(match, source, videoId) {
      let src;
      const sourceLower = source.toLowerCase();
      if (sourceLower === "youtube") {
        src = `https://www.youtube.com/embed/${videoId}`;
      } else if (sourceLower === "vimeo") {
        src = `https://player.vimeo.com/video/${videoId}`;
      }
      const attributes = [
        `src="${src}"`,
        `width="${500}"`,
        `height="${256}"`,
        `frameborder="${0}"`,
        "webkitallowfullscreen",
        "mozallowfullscreen",
        "allowfullscreen"
      ].join(" ");
      return `<iframe ${attributes}></iframe>`;
    }
  },
  {
    type: "output",
    regex: /{color: ?([^/;}]+)?\/([^/;}]+)?}/gm,
    replace: (match, fgColor, bgColor) => {
      const style = [
        simpleXssValidator.validateColor(fgColor) ? `color: ${fgColor}` : "",
        simpleXssValidator.validateColor(bgColor) ? `background-color: ${bgColor}` : ""
      ].filter(Boolean).join("; ");
      return `<span class="color" style="${style}">`;
    }
  },
  {
    type: "output",
    regex: /\{color}/gm,
    replace: () => "</span>"
  },
  {
    type: "output",
    filter: function(source) {
      return source.replace(
        showdownMatchers.codeTags.matcher,
        showdownMatchers.codeTags.replacer
      );
    }
  },
  {
    type: "output",
    filter: function(source) {
      return source.replace(
        showdownMatchers.escapedStrikethrough.matcher,
        showdownMatchers.escapedStrikethrough.replacer
      );
    }
  },
  {
    type: "output",
    filter: function(source) {
      return source.replace(
        showdownMatchers.escapedPipe.matcher,
        showdownMatchers.escapedPipe.replacer
      );
    }
  },
  {
    type: "output",
    // @ts-expect-error this regex is bogus. you can't put a backreference/capture (\2) in a character set ([]). the intent seems to have been to capture anything that's not the closing quote, whether it was a single or double quotation mark. nevertheless, it works fine because it's not gonna find the special character \u0002 anywhere, so ([^\2]*?) gets skipped over
    regex: /<a([^>]*?)href\s*=\s*(['"])([^\2]*?)\2\1*>(.*?)<\/a>/gim,
    // a tags with href attribute
    replace: function(match) {
      const link = htmlStringToDomNode(match);
      const href = urlUtils.sanitizeUrl(
        link && link.getAttribute("href") || ""
      );
      const title = escapeHTML(link && link.getAttribute("title"));
      const text = escapeHTML(link && link.textContent);
      const attributes = [];
      if (href.startsWith("comp://")) {
        const ids = href.substr("comp://".length).split("/");
        if (ids.length === 2) {
          const [wsId, compId] = ids;
          attributes.push(
            `href="${wsId ? `/app/view/reader/workspace/${wsId}/component/${compId}` : compId}"`
          );
          attributes.push(`id="${compId}"`);
          attributes.push('class="inlineComp"');
        }
      } else {
        if (href.startsWith("/") || href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:")) {
          attributes.push(`href="${href}"`);
        } else {
          attributes.push(`href="https://${href}"`);
        }
        attributes.push('rel="nofollow noreferrer"');
        if (!href.startsWith("mailto:")) {
          attributes.push('target="_blank"');
        }
      }
      if (title) attributes.push(`title="${title}"`);
      return `<a ${attributes.join(" ")}>${text}</a>`;
    }
  }
];
const getEscapedPipesRemoved = (str) => {
  return str == null ? void 0 : str.replace(/\\\|/g, "");
};
const addAlignmentToLegacyTables = (markdown) => {
  const splitByRowDelimiter = markdown.replace(/\|\\n\|/g, "|\n|").split(/\|\n\|/g);
  const processedSections = [];
  let tableRows = [];
  let inTable = false;
  splitByRowDelimiter.filter((section) => !!section).forEach((section, index) => {
    const cleanedSection = getEscapedPipesRemoved(section);
    if (cleanedSection.includes("|")) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      const parts = section.split("|\n");
      parts.forEach((part, partIndex) => {
        if (part) {
          if (inTable) {
            tableRows = [...tableRows, part];
          }
          const isTableEnd = partIndex === 0 && parts.length > 1 || part.endsWith("|") && index === splitByRowDelimiter.length - 1;
          if (isTableEnd) {
            const formattedTable = processTable(tableRows);
            processedSections.push(formattedTable);
            tableRows = [];
            inTable = false;
          }
          if (!isTableEnd && partIndex > 0 && tableRows.length === 0) {
            tableRows = [part];
            inTable = true;
          }
        }
      });
    } else {
      if (inTable) {
        const formattedTable = processTable(tableRows);
        processedSections.push(formattedTable);
        tableRows = [];
        inTable = false;
      }
      processedSections.push(section);
    }
  });
  if (inTable && tableRows.length && !!tableRows[0]) {
    const formattedTable = getEscapedPipesRemoved(tableRows[0]).includes("|") ? processTable(tableRows) : tableRows[0];
    processedSections.push(formattedTable);
  }
  return processedSections.join("|\n");
};
const processTable = (tableRows) => {
  if (!tableRows.length) {
    return "";
  }
  const columnCount = countColumns(tableRows[0]);
  const hasAlignmentRow = tableRows.some(
    (row) => row.split("|").filter((cell) => cell.length).map((cell) => cell.trim()).every((cell) => /^:?-{3,}:?$/.test(cell))
  );
  if (!hasAlignmentRow) {
    const alignmentRow = `${new Array(columnCount).fill(" --- ").join("|")}`;
    const newTableRows = [tableRows[0], alignmentRow, ...tableRows.slice(1)];
    return newTableRows.join("|\n|");
  }
  return tableRows.join("|\n|");
};
const countColumns = (row) => {
  return getEscapedPipesRemoved(row).split("|").length - 1;
};
const converterOptions = {
  tasklists: true,
  smoothLivePreview: true,
  parseImgDimension: true,
  noHeaderId: true,
  strikethrough: true,
  tables: true
};
const tableExtension = () => [
  {
    type: "lang",
    filter
  }
];
const converter$1 = new Showdown.Converter(converterOptions);
const filter = (text) => {
  const result = text.match(/^\s*\|/gim);
  if (!text || !result || !result.length) {
    return text;
  }
  let out = "";
  const startIndex = /^\s*\|/gim.exec(text).index;
  let cellContent = "", cellCount = 0, firstRowCellCount = 0, rowCount = 0;
  let alignments = [];
  out = text.substring(0, startIndex);
  let rows = text.substring(startIndex).split("\n").filter((row) => row.trim());
  const alignmentRowIndex = rows.findIndex(
    (row) => row.split("|").filter((cell) => cell.length).map((cell) => cell.trim()).every((cell) => /^:?-{3,}:?$/.test(cell))
  );
  const headerRowsCombined = rows.slice(0, alignmentRowIndex).join("\n");
  rows = [headerRowsCombined, ...rows.slice(alignmentRowIndex)];
  alignments = rows[1].trim().split("|").map((alignment) => alignment.trim()).filter((alignment) => alignment.length > 0).map((alignment) => {
    if (alignment.startsWith(":") && alignment.endsWith(":")) {
      return "center";
    } else if (alignment.startsWith(":")) {
      return "left";
    } else if (alignment.endsWith(":")) {
      return "right";
    }
    return "left";
  });
  for (let i = startIndex; i < text.length; i++) {
    const char = text[i], colTag = rowCount === 0 ? "th" : "td";
    if (i === 0 && char === "|" || i > 0 && text[i - 1] !== "\\" && char === "|") {
      const isEndOfString = i + 1 >= text.length;
      let isNewRow = false;
      if (rowCount === 0 && firstRowCellCount === 0) {
        out += "<table><thead>";
      }
      if (rowCount === 1) {
        i += rows[1].length;
        rowCount++;
        cellCount = 0;
        continue;
      }
      if (cellCount === 0) {
        out += "<tr>";
      }
      if (cellCount > 0) {
        let mdCellContent = converter$1.makeHtml(cellContent);
        if (mdCellContent && mdCellContent.length > 6) {
          mdCellContent = mdCellContent.replace(/^<p>(.*?)<\/p>$/i, "$1");
        } else {
          mdCellContent = "";
        }
        out += mdCellContent;
        out += `</${colTag}>`;
        cellContent = "";
      }
      const alignment = alignments[cellCount];
      const alignAttr = alignment && alignment !== "left" ? ` align="${alignment}"` : "";
      out += `<${colTag}${alignAttr || ""}>`;
      cellCount++;
      if (rowCount === 0) {
        firstRowCellCount = cellCount;
        if (text[i + 1].match(/\n/)) {
          isNewRow = true;
        }
      } else if (cellCount >= firstRowCellCount) {
        if (text[i + 1].match(/\n/)) {
          isNewRow = true;
        }
      }
      if (isNewRow || isEndOfString) {
        const restOfTable = text.substring(i + 1);
        out = out.substring(0, out.length - 4);
        if (isEndOfString || text[i + 2].match(/\n|</) || restOfTable && restOfTable.indexOf("|") === -1) {
          out += `</tr>${rowCount ? "</tbody>" : "</thead>"}</table>`;
          out += restOfTable;
          break;
        } else {
          rowCount++;
          cellCount = 0;
          out += `</tr>${rowCount === 1 ? "</thead><tbody>" : ""}`;
        }
      }
    } else {
      cellContent += char;
    }
  }
  out = filter(out);
  return out;
};
Showdown.extension("ardoq", ardoqExtension);
Showdown.extension("table", tableExtension);
Showdown.subParser(
  "encodeCode",
  (text) => Showdown.helper.escapeCharacters(text, "*_{}[]\\", false)
);
const converter = new Showdown.Converter({
  ...converterOptions,
  extensions: ["ardoq", "table"]
});
const markdownToSanitizedHTML = (markdownText, sanitationLevel) => {
  if (!markdownText) return "";
  const brReplacement = new RegExp("§br§", "gm");
  const brEscapedMd = addAlignmentToLegacyTables(markdownText).replace(
    / {2}\n/gm,
    `${brReplacement.source}
`
  );
  const brEscapedHtml = converter.makeHtml(brEscapedMd);
  const html = brEscapedHtml.replace(brReplacement, "<br>\n");
  const sanitizedHTML = sanitize(html, sanitationLevel);
  return sanitizedHTML;
};
const markdownToTextPreview = (markdownText) => new DOMParser().parseFromString(
  markdownToSanitizedHTML(markdownText.slice(0, 500), "permissive"),
  "text/html"
).documentElement.textContent || "";
const StyledHTML = styled.div`
  line-height: 1.4;
  white-space: normal;

  h1 {
    ${typographyMixins.header1}
    font-size: 24px;
    line-height: 28px;
    margin: 20px 0 20px;
    padding-bottom: 7px;
  }

  h2 {
    ${typographyMixins.header2}
    font-size: 22px;
    line-height: 23px;
    margin: 27px 0 19px;
    padding-bottom: 7px;
  }

  h3 {
    ${typographyMixins.header3}
    font-size: 20px;
    margin: 26px 0 9px;
    line-height: 18px;
  }

  h4 {
    ${typographyMixins.header4}
    font-size: 18px;
    margin: 16px 0 7px;
    line-height: 18px;
  }

  h5 {
    ${typographyMixins.header5}
    text-transform: none;
    font-size: 16px;
    line-height: 17px;
    margin: 14px 0 0;
  }

  h6 {
    font-size: 14px;
    line-height: 17px;
    margin: 9px 0 -2px;
  }

  hr {
    overflow: hidden;
  }
  blockquote {
    margin: 0;
    border-left: 4px solid ${colors.grey80};
    padding: 0 ${spacing.s16};
    color: #999;
  }

  blockquote > :first-child {
    margin-top: 0;
  }

  blockquote > :last-child {
    margin-bottom: 14px;
  }

  pre {
    font-weight: 400;
    font-size: 13px;
    line-height: 160%;
    font-family: Courier, monospace;
    margin: 2px 0 8px;
    padding: 18px;
    background-color: #f5f7f8;
  }

  code {
    font-family: Courier, monospace;
    font-weight: 400;
    font-size: 14px;
    color: #c1798b;
    background-color: #f9f2f4;
    padding: 2px 3px;
  }

  pre code {
    padding: 0;
    color: inherit;
    white-space: pre-wrap;
    background-color: transparent;
  }

  img {
    vertical-align: top;
    max-width: 100%;
  }

  ul,
  ol {
    display: block;
    padding-left: ${spacing.s24};
    margin: 6px 0 10px;
  }

  ul > li {
    list-style: disc;
  }

  ol > li {
    list-style: decimal;
  }

  ul ul,
  ul ol,
  ol ol,
  ol ul {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }

  ul p,
  ol p {
    margin: 0;
  }

  ol li::marker {
    color: #aaaaaa;
  }

  a {
    color: ${colors.blue50};
    text-decoration: underline;
  }

  table {
    width: 100%;
    color: ${colors.black};
    border-collapse: collapse;
    th,
    td {
      padding: 5px 14px 5px 12px;
      height: 32px;
    }

    th {
      ${normal16};
      background-color: ${colors.grey15};
      border: 1px solid ${colors.grey80};
      color: ${colors.white};
    }

    td {
      border: 1px solid ${colors.grey80};
    }
  }
`;
const MarkdownViewer = forwardRef(
  (props, ref) => {
    const { content, transformHTML, sanitationLevel, ...rest } = props;
    const sanitizedHTML = markdownToSanitizedHTML(content, sanitationLevel);
    const html = transformHTML ? transformHTML(sanitizedHTML) : sanitizedHTML;
    return /* @__PURE__ */ jsx(
      StyledHTML,
      {
        ref,
        dangerouslySetInnerHTML: { __html: html },
        ...rest
      }
    );
  }
);
export {
  MarkdownViewer,
  htmlToMarkdown,
  markdownToSanitizedHTML,
  markdownToTextPreview
};
//# sourceMappingURL=index.js.map
